/**
 * Bank account panel for an operator-initiated deposit.
 *
 * The admin counterpart of the customer console's own `BankAccountInfo`: it shows the account
 * the transfer must reach and, next to it, the `WIC-PAY-…` reference that ties an incoming
 * transfer back to this deposit. Every field is one click away from the clipboard because an
 * operator is usually retyping these into a banking app on another screen.
 *
 * There is deliberately no fallback account: the caller passes details or renders nothing, so a
 * misconfigured platform can never put an account number nobody owns in front of an operator.
 */

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Copy, Landmark } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currency";
import type { TenantDepositBankDetails } from "@/types/tenant-deposit";

interface BankAccountInfoProps {
  amount: number;
  currency: string;
  bankDetails: TenantDepositBankDetails;
  /**
   * The payment's own number, to be typed into the transfer description. The panel only claims
   * a reference is required when one is actually on screen.
   */
  referenceCode?: string;
}

export function BankAccountInfo({
  amount,
  currency,
  bankDetails,
  referenceCode,
}: BankAccountInfoProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    void navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Card className="border-2 border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark className="h-6 w-6 text-brand" />
          Bank transfer details
        </CardTitle>
        <CardDescription>
          Transfer the exact amount to this account, quoting the reference below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg bg-brand-soft p-4">
          <div className="mb-1 text-sm text-muted-foreground">
            Amount to transfer
          </div>
          <div className="text-3xl font-bold tabular-nums text-primary">
            {formatCurrency(amount, currency)}
          </div>
        </div>

        <div className="space-y-3">
          <BankDetailRow
            label="Bank"
            value={bankDetails.bankName}
            onCopy={() => copyToClipboard(bankDetails.bankName, "Bank")}
            copied={copiedField === "Bank"}
          />
          <BankDetailRow
            label="Account number"
            value={bankDetails.accountNumber}
            onCopy={() =>
              copyToClipboard(bankDetails.accountNumber, "Account number")
            }
            copied={copiedField === "Account number"}
            highlight
          />
          <BankDetailRow
            label="Account name"
            value={bankDetails.accountName}
            onCopy={() =>
              copyToClipboard(bankDetails.accountName, "Account name")
            }
            copied={copiedField === "Account name"}
          />
          {bankDetails.branch && (
            <BankDetailRow
              label="Branch"
              value={bankDetails.branch}
              onCopy={() => copyToClipboard(bankDetails.branch!, "Branch")}
              copied={copiedField === "Branch"}
            />
          )}
          {bankDetails.swiftCode && (
            <BankDetailRow
              label="SWIFT"
              value={bankDetails.swiftCode}
              onCopy={() => copyToClipboard(bankDetails.swiftCode!, "SWIFT")}
              copied={copiedField === "SWIFT"}
            />
          )}
          {/* Highlighted like the account number: a transfer that reaches the right account
              without this is one somebody has to match by amount and timing alone. */}
          {referenceCode && (
            <BankDetailRow
              label="Transfer reference"
              value={referenceCode}
              onCopy={() => copyToClipboard(referenceCode, "Transfer reference")}
              copied={copiedField === "Transfer reference"}
              highlight
            />
          )}
        </div>

        <div className="space-y-2 rounded-lg bg-panel-2 p-4">
          {referenceCode && (
            <p className="text-sm font-semibold">
              The reference must appear in the transfer description, or the
              payment cannot be matched.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            The balance is credited only once this payment is verified from the
            payment queue.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

interface BankDetailRowProps {
  label: string;
  value: string;
  onCopy: () => void;
  copied?: boolean;
  highlight?: boolean;
}

function BankDetailRow({
  label,
  value,
  onCopy,
  copied,
  highlight,
}: BankDetailRowProps) {
  return (
    <div
      className={`flex items-center justify-between rounded-lg p-3 ${
        highlight
          ? "border border-brand bg-brand-soft-2"
          : "border border-hairline bg-panel-2"
      }`}
    >
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div
          className={`truncate font-mono ${highlight ? "text-lg font-bold" : "font-medium"}`}
          title={value}
        >
          {value}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onCopy} className="ml-2">
        {copied ? (
          <CheckCircle className="h-4 w-4 text-ok" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
